// Short videos on the Issue Register (admin page + portal Route Issues).
// A phone video is far over Netlify's 6MB function body, so it never goes through
// the function: driver-issues-sb mints a one-off signed upload URL, the browser PUTs
// the file straight into the private bucket (XHR, so we can show progress on a slow
// mobile connection), then driver-issues-sb records the path on the issue.

export const MAX_ISSUE_VIDEOS = 8;
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024; // Supabase's per-object limit

export const fmtDuration = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s) % 60).padStart(2, '0')}` : '');
export const fmtMB = (b) => (b < 104858 ? '<0.1MB' : `${(b / 1048576).toFixed(b < 10 * 1048576 ? 1 : 0)}MB`);

// Read the clip's length from its metadata. Best-effort: a codec the browser can't
// decode (HEVC on some desktops) just leaves it blank.
export function videoDuration(file) {
  return new Promise((resolve) => {
    let url = '';
    try { url = URL.createObjectURL(file); } catch { resolve(null); return; }
    const v = document.createElement('video');
    const done = (d) => { clearTimeout(t); URL.revokeObjectURL(url); resolve(d); };
    const t = setTimeout(() => done(null), 8000);
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? v.duration : null);
    v.onerror = () => done(null);
    v.src = url;
  });
}

// Refuse up front what the server would refuse — no point uploading 80MB to be told no.
export function checkVideo(file) {
  if (!file) return 'No video chosen.';
  if (file.type && !file.type.startsWith('video/')) return 'That file is not a video.';
  if (file.size > MAX_VIDEO_BYTES) {
    return `That video is ${fmtMB(file.size)} — the limit is 50MB. Trim it to the part that shows the problem (about 30 seconds).`;
  }
  return '';
}

export function putFile(url, file, type, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', type);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { resolve(); return; }
      let msg = `Upload failed (${xhr.status})`;
      try { const j = JSON.parse(xhr.responseText); if (j && j.message) msg = j.message; } catch { /* keep the status */ }
      reject(new Error(msg));
    };
    xhr.onerror = () => reject(new Error('Network error — check your signal and try again.'));
    xhr.ontimeout = () => reject(new Error('The upload timed out — try again on a better signal.'));
    xhr.timeout = 15 * 60 * 1000;
    xhr.send(file);
  });
}

// api(action, payload) → JSON; the caller passes its own (admin page vs portal).
// Resolves to the updated (signed) issue; throws with a message fit to show.
export async function uploadIssueVideo(api, issueId, file, onProgress) {
  const bad = checkVideo(file);
  if (bad) throw new Error(bad);
  // iOS sometimes hands over a .mov with an empty type.
  const type = file.type || (/\.mov$/i.test(file.name || '') ? 'video/quicktime' : 'video/mp4');
  const duration = await videoDuration(file);
  const start = await api('videoUploadUrl', { id: issueId, type, size: file.size, name: file.name });
  if (!start || !start.success) throw new Error((start && start.error) || 'Could not start the upload.');
  await putFile(start.uploadUrl, file, type, onProgress);
  const done = await api('attachVideo', { id: issueId, path: start.path, type, size: file.size, name: file.name, duration });
  if (!done || !done.success) throw new Error((done && done.error) || 'The video uploaded but could not be saved — try again.');
  return done.issue;
}
